import type { App } from 'vue';
import { createPinia } from 'pinia';
import {
	Caller,
	Realtime,
	REALTIME_KEY,
	grantedPlugin,
	phoneInputPlugin,
	piniaExtraPlugin,
	registerCaller,
	subscribeCallerError,
	useTokenStore,
	useToastStore,
	vFormPlugin,
} from '@common/index';
import router from '@/router';
import { hasStoredKioskToken, kioskDeviceHeaders, useAppModeStore } from '@/stores/AppModeStore';
import i18n from './i18n';
import vuetify from './vuetify';

export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:4000';
export const REALTIME_URL = import.meta.env.VITE_REALTIME_URL ?? API_BASE_URL;

export const pinia = createPinia();

/**
 * Canal temps réel de l'application.
 *
 * Instancié ici mais **connecté** seulement une fois la session résolue : se
 * connecter plus tôt enverrait un jeton absent et la passerelle fermerait la
 * socket. `App.vue` déclenche la connexion au bon moment.
 */
export const realtime = new Realtime({
	url: REALTIME_URL,
	path: '/realtime',
	// Relu à chaque (re)connexion : le jeton a pu être rafraîchi entre-temps.
	getToken: () => useTokenStore(pinia).token?.accessToken ?? null,
	pollingIntervalMs: 5000,
});

/**
 * Assemblage de l'application.
 *
 * L'ordre est contraint : Pinia en premier — le client HTTP et les droits
 * s'appuient sur ses stores — puis le routeur, puis les droits qui posent la
 * garde de navigation.
 */
export function registerPlugins(app: App): void {
	app.use(pinia);

	registerCaller(
		'api',
		(activePinia) =>
			new Caller(`${API_BASE_URL}/api`, activePinia, {
				useAuth: true,
				// Une borne s'annonce comme telle à chaque requête : l'API refuse une
				// session de borne présentée depuis un autre appareil.
				dynamicHeaders: kioskDeviceHeaders,
			}),
	);

	app.use(piniaExtraPlugin, { pinia, router, apiBaseUrl: `${API_BASE_URL}/api` });
	app.use(i18n);
	app.use(vuetify);
	app.use(router);
	app.use(grantedPlugin, { pinia, router });
	app.use(vFormPlugin);
	// Après Vuetify : le champ téléphone monte un `VSelect` et un `VTextField`.
	app.use(phoneInputPlugin);

	/**
	 * Pas de compte sur une borne.
	 *
	 * Une borne est un écran public vissé dans un hall : y ouvrir une session
	 * personnelle la laisserait ouverte derrière son voyageur, avec son historique
	 * de courses et son numéro. Et la garde des droits y renvoie d'elle-même dès
	 * que la session anonyme n'est pas encore ouverte — c'est ainsi qu'un
	 * formulaire de connexion apparaissait au milieu d'une commande.
	 *
	 * Déclarée **après** la garde des droits : sa redirection relance la
	 * navigation, celle-ci repasse ici, et l'accueil reprend la main — l'accueil
	 * rouvrant justement la session anonyme qui manquait.
	 */
	const KIOSK_FORBIDDEN = new Set(['login', 'register', 'account', 'ride-history']);

	router.beforeEach((to) => {
		// Le jeton mémorisé fait foi tant que le mode n'est pas résolu : la garde
		// s'exécute avant `resolve()`, et c'est justement au premier chargement
		// qu'une adresse saisie à la main passerait.
		if (!useAppModeStore(pinia).isKiosk && !hasStoredKioskToken()) {
			return true;
		}
		return KIOSK_FORBIDDEN.has(String(to.name)) ? { name: 'home' } : true;
	});

	// Toute erreur d'API non silencieuse remonte à l'utilisateur : aucune vue
	// n'a besoin de l'intercepter pour qu'elle soit visible.
	const toasts = useToastStore(pinia);
	subscribeCallerError((error) => {
		toasts.error({ message: error?.message });
	});

	app.provide(REALTIME_KEY, realtime);
}

export { router };
